import React, { Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

// Utilities
import { get } from "lodash";
import { Group } from "../../utils/logger";

// Components
import Loader from "../../components/Utilities/Loader";
import { Card, Col, Container, Row } from "react-bootstrap";

const LoggerGroup = new Group("Home");

// Suspense component requires external component to load - not inline
function Page({ cpp, user }: any) {
  const [keywordEnabled, setKeywordEnabled] = useState(false);
  const [accountPermissions, setAccountPermissions] = useState("");
  const [reportingPermissions, setReportingPermissions] = useState("");
  const [switchboardPermissions, setSwitchboardPermissions] = useState("");
  const [userManagementPermissions, setUserManagementPermissions] = useState("");

  useEffect(() => {
    const permissions: string[] = get(user, "details.permissions") || [];

    const account = permissions.find((p) => p.includes("account"));
    if (account) {
      setAccountPermissions(account);
    } else {
      setAccountPermissions("");
    }

    const switchboard = permissions.find((p) => p.includes("switchboard"));
    if (switchboard) {
      setSwitchboardPermissions(switchboard);
    } else {
      setSwitchboardPermissions("");
    }

    const reporting = permissions.find((p) => p.includes("reporting"));
    if (reporting) {
      setReportingPermissions(reporting);
    } else {
      setReportingPermissions("");
    }

    const userManagement = permissions.find((p) => p.includes("userManagement"));
    if (userManagement) {
      setUserManagementPermissions(userManagement);
    } else {
      setUserManagementPermissions("");
    }
  }, [user, user.details]);

  useEffect(() => {
    setKeywordEnabled(!!cpp.current.chatBotKey);
  }, [cpp]);

  return (
    <Container className="App-dashboard mt-2">
      <div className="App-header" data-testid="dashboard">
        <Card>
          <Card.Body>
            <Card.Title>Welcome!</Card.Title>

            <Card.Text>
              This is the CSR portal{cpp.current.title ? ` for ${cpp.current.title}` : ""}.
            </Card.Text>

            <Card.Text>
              This is where you can maintain preference selections for your clients, manage active
              preferences,
              {keywordEnabled && " test your 2-way texting flows, "}
              and run reports! Please note, not all features may be made available for all users.
            </Card.Text>
          </Card.Body>
        </Card>

        <Row xs={1} md={2} className="g-4 mt-2 row-cols-1 row-cols-md-2 justify-content-around">
          {/* ACCOUNT */}
          {accountPermissions && (
            <Col className="mb-2">
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Preference Management</Card.Title>

                  <Card.Text>
                    This is the place to do things like view communication history for a specific
                    account
                    {keywordEnabled
                      ? ", test out the 2-way workflows using the Keyword Simulator, "
                      : " "}
                    or make changes to a user's selected preferences.
                  </Card.Text>

                  <ul>
                    {accountPermissions.includes("readWrite") && (
                      <li>
                        <Link to="/pref/add">Add New Customer</Link>
                      </li>
                    )}

                    <li>
                      <Link to="/pref/search">Search Enrolled Customers</Link>
                    </li>

                    {keywordEnabled && (
                      <li>
                        <Link to="/pref/key">Keyword Simulator</Link>
                      </li>
                    )}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          )}

          {/* REPORTING */}
          {reportingPermissions && (
            <Col className="mb-2">
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Reporting</Card.Title>

                  <Card.Text>
                    This is where you can manage which reports you see and run the reports you need!
                    Note: this view is specific to you, so customize away.
                  </Card.Text>

                  <ul>
                    <li>
                      <Link to="/report/dashboard">Reporting Dashboard</Link>
                    </li>

                    {reportingPermissions.includes("readWrite") && (
                      <li>
                        <Link to="/report/change">Edit Reporting Dashboard</Link>
                      </li>
                    )}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          )}

          {/* ADMINISTRATION */}
          {(switchboardPermissions || userManagementPermissions) && (
            <Col className="mb-2">
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Administration</Card.Title>

                  <Card.Text>
                    This section allows you to manage which preference are active (should send
                    messages).
                    {userManagementPermissions &&
                      " It also allows you to manage which users have what permissions."}
                  </Card.Text>

                  <ul>
                    {switchboardPermissions && (
                      <li>
                        <Link to="/switchboard">Switchboard</Link>
                      </li>
                    )}

                    {userManagementPermissions && (
                      <li>
                        <Link to="/user-management/current-users">Manage Users</Link>
                      </li>
                    )}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </Container>
  );
}

// here app catches the suspense from page in case translations are not yet loaded
function Home(props: any) {
  useEffect(() => {
    LoggerGroup.emit("Loaded");
  });

  return (
    <Suspense fallback={<Loader />}>
      <Page {...props} />
    </Suspense>
  );
}

const mapStateToProps = (state: any) => ({
  cpp: state.cpp,
  user: state.user,
});

export default connect(mapStateToProps)(Home);
